import React, { useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';

// ─── Modal Root ─────────────────────────────────────────────────────────────

export interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
  children: React.ReactNode;
}

const SIZE_CLASSES: Record<NonNullable<ModalProps['size']>, string> = {
  sm: 'max-w-sm',
  md: 'max-w-lg',
  lg: 'max-w-2xl',
  xl: 'max-w-4xl',
};

const ModalRoot: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  size = 'md',
  className = '',
  children,
}) => {
  const handleEscape = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    },
    [onClose],
  );

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      document.addEventListener('keydown', handleEscape);
    }
    return () => {
      document.body.style.overflow = '';
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, handleEscape]);

  if (!isOpen) return null;

  const content = (
    <div
      className="fixed inset-0 bg-surface-overlay backdrop-blur-sm z-modal flex items-center justify-center animate-fade-in"
      onClick={onClose}
    >
      <div
        className={`bg-gradient-to-br from-[var(--surface-modal-from)] to-[var(--surface-modal-to)] border border-[var(--surface-modal-border)] rounded-2xl shadow-[0_25px_50px_rgba(0,0,0,0.5)] animate-scale-in w-full mx-4 ${SIZE_CLASSES[size]} ${className}`}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        {children}
      </div>
    </div>
  );

  return createPortal(content, document.body);
};

// ─── Modal.Header ───────────────────────────────────────────────────────────

export interface ModalHeaderProps {
  title?: string;
  icon?: React.ReactNode;
  onClose?: () => void;
  children?: React.ReactNode;
}

const ModalHeader: React.FC<ModalHeaderProps> = ({
  title,
  icon,
  onClose,
  children,
}) => {
  if (children) {
    return (
      <div className="px-6 py-4 border-b border-subtle flex items-center gap-3">
        {children}
      </div>
    );
  }

  return (
    <div className="px-6 py-4 border-b border-subtle flex items-center gap-3">
      {icon && (
        <div className="w-8 h-8 rounded-lg bg-brand/20 flex items-center justify-center text-brand shrink-0">
          {icon}
        </div>
      )}
      {title && (
        <h2 className="text-lg font-semibold text-content flex-1">{title}</h2>
      )}
      {onClose && (
        <button
          type="button"
          onClick={onClose}
          className="p-1.5 rounded-lg text-content-muted hover:text-content-secondary hover:bg-surface-primary cursor-pointer transition-colors border-none outline-none"
          aria-label="Close"
        >
          <X size={18} />
        </button>
      )}
    </div>
  );
};

// ─── Modal.Body ──────────────────────────────────────────────────────────────

export interface ModalBodyProps {
  className?: string;
  children: React.ReactNode;
}

const ModalBody: React.FC<ModalBodyProps> = ({ className = '', children }) => (
  <div
    className={`px-6 py-4 max-h-[70vh] overflow-y-auto ${className}`}
  >
    {children}
  </div>
);

// ─── Modal.Footer ────────────────────────────────────────────────────────────

export interface ModalFooterProps {
  className?: string;
  children: React.ReactNode;
}

const ModalFooter: React.FC<ModalFooterProps> = ({ className = '', children }) => (
  <div
    className={`px-6 py-4 border-t border-subtle flex items-center justify-end gap-3 ${className}`}
  >
    {children}
  </div>
);

// ─── Compound component export ───────────────────────────────────────────────

type ModalComponent = React.FC<ModalProps> & {
  Header: typeof ModalHeader;
  Body: typeof ModalBody;
  Footer: typeof ModalFooter;
};

const Modal = ModalRoot as ModalComponent;
Modal.Header = ModalHeader;
Modal.Body = ModalBody;
Modal.Footer = ModalFooter;

export default Modal;
